import { supabase } from "@/integrations/supabase/client";
import { handlePermissionError } from "@/lib/permissionService";

export class BaseService {
  protected tableName: string;

  constructor(tableName: string) {
    this.tableName = tableName;
  }

  protected async executeQuery<T>(
    queryFn: () => Promise<{ data: T; error: Error | { code?: string; message?: string; status?: number } | null }>
  ): Promise<T> {
    try {
      const { data, error } = await queryFn();
      
      if (error) {
        const errorCode = 'code' in error ? error.code : undefined;
        const errorMessage = 'message' in error ? error.message : error.toString();
        const errorStatus = 'status' in error ? error.status : undefined;
        
        if (errorCode === 'PGRST116' || errorMessage?.includes('RLS') || 
            errorStatus === 401 || errorStatus === 403) {
          const permissionError = handlePermissionError(error);
          throw permissionError;
        }
        throw new Error(errorMessage || 'Database operation failed');
      }
      
      return data;
    } catch (error) {
      if (error && typeof error === 'object' && 'code' in error) {
        throw error;
      }
      throw new Error(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }

  protected async queryTable<T>(
    queryBuilder: () => any
  ): Promise<T> {
    return this.executeQuery(queryBuilder);
  }

  protected async getAll<T>(options?: {
    filters?: Record<string, any>;
    orderBy?: { column: string; ascending: boolean };
  }): Promise<T[]> {
    return this.executeQuery(async () => {
      let query = (supabase as any).from(this.tableName).select('*');
      
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          query = query.eq(key, value);
        });
      }
      
      if (options?.orderBy) {
        query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending });
      }
      
      return query;
    });
  }

  protected async getById<T>(id: string): Promise<T | null> {
    return this.executeQuery(async () => {
      return await (supabase as any)
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .maybeSingle();
    });
  }

  protected async create<T>(data: Record<string, unknown>): Promise<T> {
    return this.executeQuery(async () => {
      return await (supabase as any)
        .from(this.tableName)
        .insert([data])
        .select()
        .single();
    });
  }

  protected async update<T>(id: string, data: Record<string, unknown>): Promise<T> {
    return this.executeQuery(async () => {
      return await (supabase as any)
        .from(this.tableName)
        .update(data)
        .eq('id', id)
        .select()
        .single();
    });
  }

  protected async delete(id: string): Promise<void> {
    return this.executeQuery(async () => {
      return await (supabase as any)
        .from(this.tableName)
        .delete()
        .eq('id', id);
    });
  }
}